import React, { useState, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@/components/ui/input';

/**
 * A password <Input/> with a built-in show/hide eye toggle on the right edge.
 *
 * Drop-in replacement for `<Input type="password" ... />` — accepts every prop
 * the underlying Input does. The toggle button is `type="button"` so it never
 * accidentally submits a surrounding form, and it inherits the input's
 * disabled state.
 *
 *   <PasswordInput
 *     id="pw-current"
 *     value={pw}
 *     onChange={(e) => setPw(e.target.value)}
 *     autoComplete="current-password"
 *     data-testid="pw-current-input"
 *   />
 *
 * We deliberately DON'T add this to Login.jsx / Register.jsx — those pages
 * already implement their own inline toggle with the same visual language.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = '', 'data-testid': testId, disabled, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const toggleTestId = testId ? `${testId}-toggle` : 'password-visibility-toggle';

  return (
    <div className="relative">
      <Input
        ref={ref}
        {...rest}
        disabled={disabled}
        type={visible ? 'text' : 'password'}
        // Extra right padding so the typed value never sits under the eye button.
        className={`pr-10 ${className}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none focus:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        tabIndex={-1}
        data-testid={toggleTestId}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
